import React, { useState } from 'react';
import { Modal, Input } from 'antd';
import { message } from '../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { TextArea } = Input;

interface AdminReasonModalProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  onCancel: () => void;
  // Throwing keeps the modal open (with its error shown by the caller);
  // resolving leaves closing/reloading up to the caller too, same
  // callback-driven contract as ReviewModal's onSubmitted.
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * Shared "confirm this action, but first explain why" modal -- every admin
 * action that records a reason in the audit log (suspend, unpublish, hide,
 * override, ...) uses this instead of a bespoke modal per section.
 */
const AdminReasonModal: React.FC<AdminReasonModalProps> = ({ open, title, confirmLabel, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setReason('');
    onCancel();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.ADMIN_REASON_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      setReason('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={title} open={open} onCancel={handleClose} onOk={handleSubmit} okText={confirmLabel} confirmLoading={submitting}>
      <TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.ADMIN_REASON_PLACEHOLDER)}
        maxLength={500}
      />
    </Modal>
  );
};

export default AdminReasonModal;
